<script lang="ts">
	import CourseOfferingsTable from './_components/course-offerings-table.svelte';
	import TeacherCard from '$lib/components/app/teacher-card.svelte';
	import Stars from '$lib/components/app/review/stars.svelte';
	import { parseDate, today } from '@internationalized/date';
	import * as Accordion from '$lib/components/ui/accordion';
	import * as Popover from '$lib/components/ui/popover';
	import { PUBLIC_BASE_URL } from '$env/static/public';
	import { minimumReviewAmount } from '$lib/constants';
	import { Button } from '$lib/components/ui/button';
	import * as Table from '$lib/components/ui/table';
	import { Badge } from '$lib/components/ui/badge';
	import Check from '@lucide/svelte/icons/check';
	import Info from '@lucide/svelte/icons/info';
	import X from '@lucide/svelte/icons/x';
	import { Image } from '@unpic/svelte';
	import SvelteSeo from 'svelte-seo';
	import { page } from '$app/state';

	let { data } = $props();

	function scrollToDates(e: MouseEvent) {
		e.preventDefault();

		const termineSection = document.getElementById('termine');
		if (termineSection) {
			termineSection.scrollIntoView({ behavior: 'smooth' });
		}
	}

	const teachers = $derived(data.courseData.teacherCourses.map((v) => v.teacher));
</script>

<SvelteSeo jsonLd={data.jsonLd} />

<main class="flex flex-1 flex-col gap-16 py-16 md:gap-36 md:pb-36">
	<section class="flex grid-cols-2 flex-col-reverse gap-8 md:grid md:gap-16">
		<div class="flex flex-col justify-center">
			<h1 class="pb-3 text-3xl font-semibold">
				{data.courseData.name} Ausbildung
			</h1>
			<div class="flex flex-col gap-8">
				<div class="flex flex-wrap items-center gap-x-4 gap-y-2">
					{#if data.courseData.reviews.length >= minimumReviewAmount}
						<div class="flex flex-wrap items-center gap-1 gap-x-3">
							<Stars stars={data.courseData.averageReview} />
							<a class="underline" href="{page.url.pathname}/bewertungen"
								>{data.courseData.reviews.length}
								{data.courseData.reviews.length > 1 ? 'Bewertungen' : 'Bewertung'}</a
							>
						</div>
					{/if}
					{#if data.courseData.stateRegistrationId}
						<div class="flex items-center">
							<Badge variant="secondary" class="bg-asge-200 hover:bg-asge-200"
								>Staatlich zugelassen</Badge
							>
						</div>
					{/if}
					{#if parseDate(data.courseData.createdAt.split(' ')[0]) > today('Europe/Berlin').subtract( { weeks: 2 } )}
						<div class="flex items-center">
							<Badge class="whitespace-nowrap bg-orange-200" variant="secondary">Neu</Badge>
						</div>
					{/if}
				</div>
				<div class="flex flex-col space-y-1 py-2">
					<Table.Root>
						<Table.Body>
							<Table.Row class="hover:bg-background">
								<Table.Cell class="pl-0 font-semibold">Preis</Table.Cell>
								<Table.Cell class="pr-0"
									>{new Intl.NumberFormat('de-DE', {
										style: 'currency',
										currency: 'EUR'
									}).format(data.courseData.priceInCents / 100)}</Table.Cell
								>
							</Table.Row>
							<Table.Row class="hover:bg-background">
								<Table.Cell class="pl-0 font-semibold">Dauer</Table.Cell>
								<Table.Cell class="pr-0"
									>{data.courseData.defaultDuration}
									{data.courseData.defaultDuration > 1 ? 'Tage' : 'Tag'}</Table.Cell
								>
							</Table.Row>
							{#if data.courseData.requirements.length > 0}
								<Table.Row class="hover:bg-background">
									<Table.Cell class="pl-0 font-semibold"
										>{data.courseData.requirements.length > 1
											? 'Voraussetzungen'
											: 'Voraussetzung'}</Table.Cell
									>
									<Table.Cell class="flex flex-wrap gap-1">
										{#each data.courseData.requirements as requirement, i}
											<a
												href="/ausbildungen/{requirement.requirement.category.slug}/{requirement
													.requirement.slug}"
												class="underline"
											>
												{#if i < data.courseData.requirements.length - 1}
													{requirement.requirement.name},
												{:else}
													{requirement.requirement.name}
												{/if}
											</a>
										{/each}
										{#if data.courseData.requirementsNeeded !== data.courseData.requirements.length}
											({data.courseData.requirementsNeeded} davon benötigt)
										{/if}
									</Table.Cell>
								</Table.Row>
							{/if}
							{#if data.courseData.stateRegistrationId}
								<Table.Row class="hover:bg-background">
									<Table.Cell class="pl-0 font-semibold">ZFU-Zulassungsnummer</Table.Cell>
									<Table.Cell class="pr-0">{data.courseData.stateRegistrationId}</Table.Cell>
								</Table.Row>
							{/if}
							<Table.Row class="hover:bg-background">
								<Table.Cell class="pl-0 font-semibold">Qualifikationsstufe</Table.Cell>
								<Table.Cell class="pr-0"
									>DQR/EQR Qualifikationsstufe {data.courseData.qualificationLevel}, DIN-Norm 33961
									Niveau
									{data.courseData.qualificationLevel}</Table.Cell
								>
							</Table.Row>
							<Table.Row class="hover:bg-background">
								<Table.Cell class="pl-0 font-semibold"
									><div class="flex items-center gap-2">
										Abschlussprüfung <Popover.Root>
											<Popover.Trigger>
												{#snippet child({ props })}
													<Button {...props} size="icon" variant="ghost" class="size-5"
														><Info class="size-4" /></Button
													>
												{/snippet}
											</Popover.Trigger>
											<Popover.Content
												class="z-40 w-fit max-w-prose text-pretty p-2 text-sm font-normal"
												>{#if data.courseData.hasExam}
													Dieser Kurs hat eine Abschlussprüfung. Diese umfasst eine schriftliche,
													praktische und eine mündliche Prüfung. Diese finden am letzten Tag der
													Ausbildung statt.
												{:else}
													Dieser Kurs hat keine Abschlussprüfung. Du erhältst am Ende der Ausbildung
													trotzdem eine Urkunde.
												{/if}</Popover.Content
											>
										</Popover.Root>
									</div>
								</Table.Cell>
								<Table.Cell class="pr-0"
									>{#if data.courseData.hasExam}
										<Check class="size-4" />
									{:else}
										<X class="size-4" />
									{/if}</Table.Cell
								>
							</Table.Row>
						</Table.Body>
					</Table.Root>
				</div>
				<Button onclick={(e) => scrollToDates(e)} href="#termine" class="w-fit"
					>Termin finden</Button
				>
			</div>
		</div>
		<div class="relative shrink-0 overflow-clip rounded-md bg-asge-800">
			<Image
				class="h-full w-full opacity-85"
				cdn="directus"
				src="{PUBLIC_BASE_URL}/api/img?objectKey={data.courseData.image.objectKey}"
				layout="constrained"
				width={2000}
				height={1700}
				alt={data.courseData.name}
			/>
		</div>
	</section>

	<section id="termine" class="full-bleed scroll-m-20 bg-asge-100 py-20">
		<div class="flex flex-col gap-8">
			<h2 class="text-xl font-semibold md:text-2xl">Termine</h2>
			{#if data.courseData.courseOfferings.length > 0}
				<CourseOfferingsTable
					data={data.courseData.courseOfferings}
					cart={data.cart}
					addToCartForm={data.addToCartForm}
					removeFromCartForm={data.removeFromCartForm}
					sportspass={false}
				/>
			{:else}
				Aktuell leider keine geplanten Termine.
			{/if}
		</div>
	</section>
	<section class="flex justify-center">
		<Accordion.Root value="item-1" class="accordion w-[65ch] text-pretty" type="single">
			<Accordion.Item value="item-1">
				<Accordion.Trigger>Allgemeine Informationen</Accordion.Trigger>
				<Accordion.Content>{@html data.courseData.description}</Accordion.Content>
			</Accordion.Item>
			<Accordion.Item value="item-2">
				<Accordion.Trigger>Themenfelder</Accordion.Trigger>
				<Accordion.Content>{@html data.courseData.subjects}</Accordion.Content>
			</Accordion.Item>
			<Accordion.Item value="item-3">
				<Accordion.Trigger>Zielgruppe</Accordion.Trigger>
				<Accordion.Content>{@html data.courseData.targetAudience}</Accordion.Content>
			</Accordion.Item>
			<Accordion.Item value="item-4">
				<Accordion.Trigger>Vorteile</Accordion.Trigger>
				<Accordion.Content>{@html data.courseData.advantages}</Accordion.Content>
			</Accordion.Item>
			{#if data.courseData.importantNotes}
				<Accordion.Item value="item-5">
					<Accordion.Trigger>Wichtige Hinweise</Accordion.Trigger>
					<Accordion.Content>{@html data.courseData.importantNotes}</Accordion.Content>
				</Accordion.Item>
			{/if}
		</Accordion.Root>
	</section>

	{#if teachers.length > 0}
		<section class="flex flex-col gap-8">
			<h2 class="text-xl font-semibold md:text-2xl">Unsere Experten</h2>
			<TeacherCard {teachers} />
		</section>
	{/if}
</main>

<style lang="postcss">
	:global(.accordion ul) {
		@apply ml-5 list-disc;
	}
</style>
